/**
 * The 100 levels (spec 03). Loaded from JSON and checked once, so a bad re-import fails at
 * start-up with the level and field named, not as a monster walking through a wall.
 */

import data from '../../content/levels/c64.json';
import type { LevelDef, LevelSet, MonsterKind, SpecialBubble } from './types';

export type { LevelDef, LevelMonster, LevelSet, MonsterKind, SpecialBubble } from './types';

const KINDS: readonly MonsterKind[] = ['zenChan', 'mighta', 'monsta', 'pulpul', 'banebou', 'hidegons', 'invader', 'drunk'];
const SPECIALS: readonly SpecialBubble[] = ['lightning', 'fire', 'water', 'extend'];

/** Throws on the first thing in `set` that the game could not play. */
export function checkLevelSet(set: LevelSet): void {
  const fail = (n: number, what: string): never => {
    throw new Error(`level ${n}: ${what}`);
  };
  set.levels.forEach((l, i) => {
    if (l.n !== i + 1) fail(l.n, `out of order at index ${i}`);
    for (const grid of [l.tiles, l.wind]) {
      if (grid.length !== set.height) fail(l.n, `has ${grid.length} rows, expected ${set.height}`);
      grid.forEach((row, r) => {
        if (row.length !== set.width) fail(l.n, `row ${r} is ${row.length} wide`);
      });
    }
    if (!l.wind.every((row) => /^[\^><v]*$/.test(row))) fail(l.n, 'wind has a character other than ^ > < v');
    for (const m of l.monsters) if (!KINDS.includes(m.kind)) fail(l.n, `unknown monster "${m.kind}"`);
    for (const s of l.specials) if (!SPECIALS.includes(s)) fail(l.n, `unknown special bubble "${s}"`);
  });
}

const set = data as unknown as LevelSet;
checkLevelSet(set);

export const LEVELS: LevelSet = set;

/** Level `n` (1-based). Throws outside 1..100. */
export function level(n: number): LevelDef {
  const l = set.levels[n - 1];
  if (!l) throw new Error(`no level ${n}`);
  return l;
}
